import { getApiHost } from '@/appSettings';
import { RestManager } from '@/newApi/services/rest/RestManager';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { QueryOptions, UseMutationOptions } from '@tanstack/vue-query';
import { queryOptions, useQueryClient } from '@tanstack/vue-query';
import { AxiosError } from 'axios';
import { ResgateManager, ResErr } from '@/newApi/services/ResgateManager';
import type { CountryInvolveSettings, CountryInvolveSettingsUpdate, CountrySettingsChanges, InstanceSettingsV2 } from '@sysarb/sysarb-types-v2/services/v1/instance';

const instanceService = new RestManager(`https://${getApiHost()}/instance-service`);

type CountrySettingsOptions = {
    queries: {
        /** The instance's per-country (and per legal entity) employee portal settings. Null when the instance has no settings yet. */
        getCountrySettings: (enabled?: boolean) => QueryOptions<InstanceSettingsV2 | null, Error>;
        /** A changeID the backend bumps whenever the rows change, including in the background after the grouping type is switched. */
        getCountrySettingsChangeId: (enabled?: boolean) => QueryOptions<CountrySettingsChanges, Error>;
    };
    mutations: {
        /** Saves one country/legal entity row. Saving also confirms it, which is what makes the settings apply. */
        updateCountrySetting: () => UseMutationOptions<CountryInvolveSettings, Error, { id: string; settings: CountryInvolveSettingsUpdate }>;
        /** Deletes a row that no active employment uses any more. The backend refuses with 409 if one still does. */
        deleteCountrySetting: () => UseMutationOptions<void, Error, { id: string }>;
    };
};

export const countrySettings: CountrySettingsOptions = {
    queries: {
        getCountrySettings: (enabled: boolean = true): QueryOptions<InstanceSettingsV2 | null, Error> => {
            const { instanceTopic } = useLoginStore();
            return queryOptions<InstanceSettingsV2 | null, Error>({
                queryKey: ['country-settings', instanceTopic],
                queryFn: async (): Promise<InstanceSettingsV2 | null> => {
                    try {
                        return await instanceService.get<InstanceSettingsV2>(`v1/${instanceTopic}/system/instance/country-settings`);
                    } catch (error) {
                        // 404 means the instance has no settings row yet, so there are no country settings either
                        if (error instanceof AxiosError && error.response?.status === 404) return null;
                        console.error('Error fetching country settings', error);
                        throw new Error('Failed to fetch country settings');
                    }
                },
                enabled: () => enabled && instanceTopic != null
            });
        },
        getCountrySettingsChangeId: (enabled: boolean = true): QueryOptions<CountrySettingsChanges, Error> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.system.instance.country-settings.changes`;
            return queryOptions<CountrySettingsChanges, Error>({
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['country-settings-change-id', topic],
                queryFn: async (): Promise<CountrySettingsChanges> => {
                    return ResgateManager.model<CountrySettingsChanges>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                enabled: () => enabled && instanceTopic != null
            });
        }
    },
    mutations: {
        updateCountrySetting: (): UseMutationOptions<CountryInvolveSettings, Error, { id: string; settings: CountryInvolveSettingsUpdate }> => {
            const queryClient = useQueryClient();
            const { instanceTopic } = useLoginStore();

            return {
                mutationFn: async ({ id, settings }): Promise<CountryInvolveSettings> =>
                    instanceService.put<CountryInvolveSettings>(settings, `v1/${instanceTopic}/system/instance/country-settings/${id}`),
                // The list drives the sidebar's "Unsaved" badges and the unsaved notification
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: ['country-settings', instanceTopic] });
                },
            };
        },
        deleteCountrySetting: (): UseMutationOptions<void, Error, { id: string }> => {
            const queryClient = useQueryClient();
            const { instanceTopic } = useLoginStore();

            return {
                mutationFn: async ({ id }): Promise<void> => {
                    await instanceService.delete<void>(`v1/${instanceTopic}/system/instance/country-settings/${id}`);
                },
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: ['country-settings', instanceTopic] });
                },
            };
        }
    }
};
